// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { UserSettings, View } from '../types';
import { MOCK_USER_CREDENTIALS, INITIAL_USER_SETTINGS, APP_MODE, UI_CONFIG, EDU_CONFIG } from '../config';
import Card from './common/Card';
import { GoogleIcon, FacebookIcon, ArrowLeftIcon } from './common/Icons';
import Spinner from './common/Spinner';
import LoadingDataIndicator from './common/LoadingDataIndicator';
import { loginWithGoogleIdToken, loginWithGoogleAccessToken, loginWithFacebook, getUserSettings, login, studentQuickLogin, setEduappAccessToken, captureReferralFromUrl, getReferralCode, clearReferralCode } from '../services/apiService';
import { getEnvVar } from '../utils/env';

interface LoginProps {
    onLogin: (user: UserSettings) => void;
    onBack: () => void;
    onNavigate: (view: View) => void;
    isLoadingData?: boolean;
}

const Login: React.FC<LoginProps> = ({ onLogin, onBack, onNavigate, isLoadingData }) => {
    // Đọc env vars trong component để tránh lỗi ambient context
    const googleClientId = getEnvVar('VITE_GOOGLE_CLIENT_ID');
    const facebookAppId = getEnvVar('VITE_FACEBOOK_APP_ID');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | null>(null);
    const [loginMode, setLoginMode] = useState<'standard' | 'student_code'>('standard');
    const [studentCode, setStudentCode] = useState('');
    const [studentDobDigits, setStudentDobDigits] = useState('');
    const [securityQuestion, setSecurityQuestion] = useState<string | null>(null);
    const [securityAnswer, setSecurityAnswer] = useState('');
    const [isGoogleReady, setIsGoogleReady] = useState(false);
    const googleInited = useRef(false);
    const fbInited = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Tự động cuộn để khối đăng nhập nằm giữa màn hình khi component được mount
        if (containerRef.current) {
            containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => { captureReferralFromUrl(); }, []);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);
        setIsLoading(true);

        if (loginMode === 'student_code') {
            try {
                // PRODUCTION: Gọi API backend để xác thực (có request thực)
                if (APP_MODE === 'PRODUCTION') {
                    const { student, access_token } = await studentQuickLogin(studentCode, studentDobDigits, securityAnswer || undefined);
                    setEduappAccessToken(access_token, student.email, 'student_code');
                    const user: UserSettings = {
                        ...INITIAL_USER_SETTINGS,
                        teacherName: student.name,
                        email: student.email || `student_${studentCode}@code.login`,
                        role: 'student',
                        authType: 'student_code',
                        studentProfileDescription: `Đăng nhập mã - quản lý bởi giáo viên`
                    };
                    onLogin(user);
                } else if (typeof (window as any).handleStudentCodeLogin === 'function') {
                    // DEVELOPMENT: Fallback tìm trong students (nếu có)
                    const student = await (window as any).handleStudentCodeLogin(studentCode, studentDobDigits);
                    if (student) {
                        const user: UserSettings = {
                            ...INITIAL_USER_SETTINGS,
                            teacherName: student.name,
                            email: student.email || `student_${studentCode}@quick.login`,
                            role: 'student',
                            studentProfileDescription: `Quick login via code ${studentCode}`
                        };
                        onLogin(user);
                    } else {
                        throw new Error('Mã đăng nhập không chính xác hoặc đã hết hạn.');
                    }
                } else {
                    throw new Error('Hệ thống đăng nhập nhanh đang bảo trì. Chuyển sang PRODUCTION để dùng API.');
                }
            } catch (err: any) {
                if (err?.requiresSecurityAnswer && err?.securityQuestion) {
                    setSecurityQuestion(err.securityQuestion);
                    setError('Vui lòng trả lời câu hỏi bảo mật bên dưới.');
                } else {
                    setError(err?.message || 'Đăng nhập thất bại.');
                }
                setIsLoading(false);
            }
            return;
        }

        try {
            // Luôn gọi backend để xác thực
            await login(username, password);
            // Thay vì gọi getUserSettings() ở đây, chúng ta truyền thông tin tối thiểu
            // App.tsx sẽ tự động nạp full settings và data qua loadProductionData()
            // role: null để user mới luôn thấy màn chọn vai trò (kể cả khi API lỗi)
            onLogin({ ...INITIAL_USER_SETTINGS, email: username, role: null } as UserSettings);
        } catch (err) {
            setError((err as Error).message);
            setIsLoading(false);
        }
    };

    const handleFillTeacher = () => {
        setUsername(MOCK_USER_CREDENTIALS.teacher.username);
        setPassword(MOCK_USER_CREDENTIALS.teacher.password);
    };

    const handleFillParent = () => {
        setUsername(MOCK_USER_CREDENTIALS.parent.username);
        setPassword(MOCK_USER_CREDENTIALS.parent.password);
    };

    const handleFillStudent = () => {
        setUsername(MOCK_USER_CREDENTIALS.student.username);
        setPassword(MOCK_USER_CREDENTIALS.student.password);
    };

    const handleFillNewUser = () => {
        setUsername('new.user@dev.com');
        setPassword('password'); // any password works for this mock
    };

    const GOOGLE_BIRTHDAY_SCOPE = 'https://www.googleapis.com/auth/user.birthday.read';
    const tokenClientRef = useRef<any>(null);
    const useBirthdayScope = EDU_CONFIG?.GOOGLE_BIRTHDAY_SCOPE_ENABLED === true;

    // GSI (id_token) + Token Client (birthday scope khi bật) + FB SDK load & init (chỉ khi PRODUCTION)
    useEffect(() => {
        if (APP_MODE !== 'PRODUCTION') return;
        if (googleClientId && !googleInited.current) {
            const s = document.createElement('script');
            s.src = 'https://accounts.google.com/gsi/client';
            s.async = true;
            s.defer = true;
            s.onload = () => {
                const g = (window as any).google;
                if (g?.accounts?.id) {
                    g.accounts.id.initialize({
                        client_id: googleClientId,
                        use_fedcm_for_prompt: false,
                        ux_mode: 'popup',
                        itp_support: true,
                        callback: (res: { credential: string }) => {
                            setError(null);
                            setSocialLoading('google');
                            loginWithGoogleIdToken(res.credential, getReferralCode())
                                .then(() => { clearReferralCode(); onLogin({ ...INITIAL_USER_SETTINGS, role: null } as UserSettings); })
                                .catch((e) => setError((e as Error).message || 'Đăng nhập Google thất bại.'))
                                .finally(() => setSocialLoading(null));
                        },
                    });
                }
                // Chỉ dùng Token Client (scope birthday) khi cấu hình bật. Khi tắt → dùng GSI id_token.
                if (useBirthdayScope && g?.accounts?.oauth2?.initTokenClient) {
                    tokenClientRef.current = g.accounts.oauth2.initTokenClient({
                        client_id: googleClientId,
                        scope: `openid profile email ${GOOGLE_BIRTHDAY_SCOPE}`,
                        callback: (res: { access_token: string }) => {
                            if (!res?.access_token) return;
                            setError(null);
                            setSocialLoading('google');
                            loginWithGoogleAccessToken(res.access_token, getReferralCode())
                                .then(() => { clearReferralCode(); onLogin({ ...INITIAL_USER_SETTINGS, role: null } as UserSettings); })
                                .catch((e) => setError((e as Error).message || 'Đăng nhập Google thất bại.'))
                                .finally(() => setSocialLoading(null));
                        },
                    });
                }
                googleInited.current = true;
                setIsGoogleReady(true);
            };
            document.head.appendChild(s);
        }
        if (facebookAppId && !fbInited.current) {
            (window as any).fbAsyncInit = () => {
                (window as any).FB.init({ appId: facebookAppId, cookie: true, xfbml: true });
                fbInited.current = true;
            };
            const s = document.createElement('script');
            s.src = 'https://connect.facebook.net/vi_VN/sdk.js';
            s.async = true;
            s.defer = true;
            document.head.appendChild(s);
        }
    }, [onLogin, googleClientId, facebookAppId]);

    const handleGoogleButtonClick = () => {
        if (tokenClientRef.current) {
            tokenClientRef.current.requestAccessToken();
        } else if ((window as any).google?.accounts?.id) {
            (window as any).google.accounts.id.prompt((n: any) => {
                if (n.isNotDisplayed?.() || n.isSkippedMoment?.()) {
                    setError('Vui lòng sử dụng nút đăng nhập Google bên dưới.');
                }
            });
        }
    };

    // Render Google Button: Token Client (scope birthday) hoặc fallback GSI
    useEffect(() => {
        if (loginMode !== 'standard' || !isGoogleReady) return;
        const btnContainer = document.getElementById('google-button-official');
        if (!btnContainer) return;
        if (tokenClientRef.current) {
            btnContainer.innerHTML = '';
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'flex items-center justify-center gap-3 w-full py-3 px-4 border-2 border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-bold text-slate-700 disabled:opacity-50';
            btn.innerHTML = '<img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" class="w-5 h-5" /><span>Tiếp tục với Google</span>';
            btn.onclick = () => { setError(null); tokenClientRef.current?.requestAccessToken(); };
            btnContainer.appendChild(btn);
        } else if ((window as any).google?.accounts?.id) {
            btnContainer.innerHTML = '';
            (window as any).google.accounts.id.renderButton(btnContainer, { theme: 'outline', size: 'large', width: 350, text: 'continue_with', locale: 'vi' });
        }
    }, [loginMode, isGoogleReady]);

    const handleGoogleClick = () => {
        if (APP_MODE !== 'PRODUCTION' || !googleClientId) {
            setError('Chế độ PRODUCTION cần cấu hình VITE_GOOGLE_CLIENT_ID.');
            return;
        }
        setError(null);
        // Gọi prompt với callback để xử lý lỗi
        (window as any).google?.accounts?.id?.prompt((notification: any) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                // Nếu One Tap bị chặn, ta có thể yêu cầu người dùng click vào nút chính thức
                setError('Vui lòng sử dụng nút đăng nhập Google bên dưới.');
            }
        });
    };

    const handleFacebookClick = () => {
        if (APP_MODE !== 'PRODUCTION' || !facebookAppId) {
            setError('Chế độ PRODUCTION cần cấu hình VITE_FACEBOOK_APP_ID.');
            return;
        }
        if (!(window as any).FB) {
            setError('Facebook SDK chưa tải xong. Vui lòng thử lại trong giây lát.');
            return;
        }
        setError(null);
        setSocialLoading('facebook');
        (window as any).FB.login((res: { authResponse?: { userID: string; accessToken: string } }) => {
            if (!res.authResponse) {
                setError('Đăng nhập Facebook bị hủy hoặc thất bại.');
                setSocialLoading(null);
                return;
            }
            const { userID, accessToken } = res.authResponse;
            loginWithFacebook(userID, accessToken, getReferralCode())
                .then(() => {
                    clearReferralCode();
                    // role: null để user mới luôn thấy màn chọn vai trò
                    onLogin({ ...INITIAL_USER_SETTINGS, role: null } as UserSettings);
                })
                .catch((e) => setError((e as Error).message || 'Đăng nhập Facebook thất bại.'))
                .finally(() => setSocialLoading(null));
        }, { scope: 'email,public_profile' });
    };

    return (
        <div ref={containerRef} className="flex flex-col items-center justify-center py-12">
            <div className="max-w-md w-full"> 
                <Card className="p-8 md:p-10 relative overflow-hidden">
                    {(socialLoading || isLoadingData) && (
                        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
                            <LoadingDataIndicator message={isLoadingData ? 'Đang thiết lập thông tin người dùng...' : 'Đang xác thực tài khoản...'} />
                        </div>
                    )}
                    <div className="text-center">
                        {UI_CONFIG.APP_LOGO_URL && (
                            <div className="flex justify-center mb-6">
                                <a href="/" title="Trang chủ" className="flex items-center justify-center"><img src={UI_CONFIG.APP_LOGO_URL} alt="Logo" className="h-20 sm:h-24 w-auto max-w-[min(100%,280px)] object-contain" decoding="async" /></a>
                            </div>
                        )}
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Đăng nhập {UI_CONFIG.APP_NAME}</h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {UI_CONFIG.APP_SUBTITLE}
                        </p>
                    </div>

                    <div className="mt-8 flex bg-slate-100 p-1 rounded-xl">
                        <button 
                            type="button" 
                            onClick={() => { setLoginMode('standard'); setSecurityQuestion(null); setSecurityAnswer(''); setError(null); }}
                            className={`flex-1 py-2 text-xs font-black uppercase tracking-widest rounded-lg transition-all ${loginMode === 'standard' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            Người dùng chính
                        </button>
                        <button 
                            type="button" 
                            onClick={() => { setLoginMode('student_code'); setSecurityQuestion(null); setSecurityAnswer(''); setError(null); }}
                            className={`flex-1 py-2 text-xs font-black uppercase tracking-widest rounded-lg transition-all ${loginMode === 'student_code' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            Học sinh (Mã số)
                        </button>
                    </div>

                    {loginMode === 'standard' ? (
                        <>
                            <div className="mt-8 space-y-4">
                        {/* Container cho nút Google chuẩn của hệ thống */}
                        <div id="google-button-official" className="w-full flex justify-center"></div>

                        {facebookAppId && (
                            <button
                                type="button"
                                disabled={!(APP_MODE === 'PRODUCTION' && facebookAppId) || socialLoading !== null}
                                onClick={handleFacebookClick}
                                className="w-full flex justify-center items-center gap-3 py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {socialLoading === 'facebook' ? <Spinner size="sm" /> : <FacebookIcon className="h-5 w-5" />}
                                <span>{socialLoading === 'facebook' ? 'Đang đăng nhập...' : 'Đăng nhập với Facebook'}</span>
                            </button>
                        )}
                    </div>

                    <div className="mt-6 text-center text-xs text-gray-500 px-4">
                        Bằng việc tiếp tục, bạn đồng ý với <button type="button" onClick={() => onNavigate(View.TermsOfUse)} className="underline hover:text-gray-700">Điều khoản sử dụng</button> của chúng tôi. Đọc <button type="button" onClick={() => onNavigate(View.PrivacyPolicy)} className="underline hover:text-gray-700">Chính sách quyền riêng tư</button> của chúng tôi.
                    </div>
                </>
                    ) : (
                        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                            <div className="text-center p-6 bg-sky-50 rounded-2xl border border-sky-100 mb-6">
                                <p className="text-xs text-sky-700 font-bold leading-relaxed">
                                    💡 Bảo mật 2 lớp: Nhập mã 8 ký tự (A-Z, 0-9) và Ngày/Tháng sinh (Ví dụ: 15/05 → 1505).
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="studentCode" className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                                        Mã 8 ký tự (A-Z, 0-9)
                                    </label>
                                    <input
                                        id="studentCode"
                                        name="studentCode"
                                        type="text"
                                        maxLength={8}
                                        placeholder="ABCD12XY"
                                        required
                                        value={studentCode}
                                        onChange={(e) => setStudentCode(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase())}
                                        className="w-full text-center text-2xl font-black tracking-[0.2em] py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-teal-500 focus:bg-white transition-all outline-none"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">Đủ 8 ký tự mới bấm được</p>
                                </div>
                                <div>
                                    <label htmlFor="studentDobDigits" className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                                        Ngày & Tháng sinh (4 số DDMM)
                                    </label>
                                    <input
                                        id="studentDobDigits"
                                        name="studentDobDigits"
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={4}
                                        placeholder="1505"
                                        required
                                        value={studentDobDigits}
                                        onChange={(e) => setStudentDobDigits(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                        className="w-full text-center text-2xl font-black tracking-[0.2em] py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl focus:border-teal-500 focus:bg-white transition-all outline-none"
                                    />
                                    <p className="text-[10px] text-slate-400 mt-1">VD: 1505 = 15/05</p>
                                </div>
                            </div>

                            {securityQuestion && (
                                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                                    <label className="block text-[10px] font-black text-amber-700 uppercase tracking-widest mb-2">Câu hỏi bảo mật của lớp</label>
                                    <p className="text-sm font-bold text-amber-800 mb-2">{securityQuestion}</p>
                                    <input
                                        type="text"
                                        placeholder="Nhập câu trả lời"
                                        value={securityAnswer}
                                        onChange={(e) => setSecurityAnswer(e.target.value)}
                                        className="w-full p-3 bg-white border-2 border-amber-200 rounded-xl focus:border-amber-500 text-sm"
                                    />
                                    <button type="button" onClick={() => { setSecurityQuestion(null); setSecurityAnswer(''); setError(null); }} className="mt-2 text-xs text-amber-600 hover:underline">Bỏ qua / Thử lại</button>
                                </div>
                            )}

                            {error && <p className="text-sm text-red-600 text-center">{error}</p>}

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading || studentCode.length < 8 || studentDobDigits.length < 4 || (!!securityQuestion && !securityAnswer.trim())}
                                    className="w-full flex justify-center py-4 px-4 border border-transparent rounded-2xl shadow-xl text-sm font-black uppercase tracking-widest text-white bg-teal-600 hover:bg-teal-700 disabled:bg-slate-200 transition-all active:scale-95"
                                >
                                    {isLoading ? <Spinner size="sm" /> : 'Vào học ngay'}
                                </button>
                            </div>
                        </form>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default Login;